/**
 * Format a talk's start or end in the talk's own timezone.
 *
 * Formatting in whatever timezone the code runs in gave two answers for the
 * same talk: the server renders in UTC, a browser in Bonn one or two hours
 * later - and both were labelled with the talk's timezone. Naming the zone
 * explicitly makes the server, every browser and the label agree.
 *
 * The 66 talks imported from the old site have no time of day - the pages
 * they came from give only a date - so they carry midnight UTC, which says
 * "date only". Those show the date alone, read in UTC so it cannot slip to
 * the previous day.
 */
const DATE_ONLY = /T00:00(:00)?(\.0+)?(Z|\+00:?00)$/;
const DEFAULT_TIMEZONE = 'Europe/Berlin';

const parts = (date, options) => {
  const byType = {};
  new Intl.DateTimeFormat('en-US', options)
    .formatToParts(date)
    .forEach(({ type, value }) => {
      byType[type] = value;
    });
  return byType;
};

export const formatTalkDateTime = (value, timeZone) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const dateOnly = DATE_ONLY.test(value);
  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...(dateOnly
      ? { timeZone: 'UTC' }
      : { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
  };
  let p;
  try {
    p = parts(date, {
      ...options,
      timeZone: dateOnly ? 'UTC' : timeZone || DEFAULT_TIMEZONE,
    });
  } catch (e) {
    // An unknown timezone name typed into the field.
    p = parts(date, { ...options, timeZone: DEFAULT_TIMEZONE });
  }
  const day = `${p.month} ${p.day}, ${p.year}`;
  return dateOnly ? day : `${day}, ${p.hour}:${p.minute}`;
};

export default formatTalkDateTime;
